import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { ParsedRowType, ParseAlgorithmDto, BindingDataDto } from "../../../types";
import { rawDataConvert } from "../../../services/rawDataService";
import { findInsertIndex, getDependentIds, recomputeTextValue, computeTextValue, createEmptyRow } from "../../../utils/hexParser/treeUtils";

export const useHexDataParser = (
  hexData: string,
  open: boolean,
  rules?: ParseAlgorithmDto[],
  bindingRules?: BindingDataDto[]
) => {
  const [rawData, setRawData] = useState<string>(hexData);
  const [rawBaseData, setBaseRawData] = useState<string>(hexData);
  const [parsed, setParsed] = useState<ParsedRowType[]>([]);
  const hasFetchedConverted = useRef(false);

  const loadParseAlgorithm = async (): Promise<ParsedRowType[] | undefined> => {
    if (!rules || rules.length === 0) return;

    const parsedArray: ParsedRowType[] = rules.map(item => {
      const id = (item.id ?? uuidv4()).toString();
      const baseId =
        item.baseId != null
          ? item.baseId.toString()
          : item.parentItemId != null
            ? item.parentItemId.toString()
            : undefined;

      return {
        id,
        start: item.startIndex,
        end: item.endIndex,
        typeId: item.dataTypeId,
        toTypeId: item.toDataTypeId,
        parameterType: item.parameterType,
        description: item.description,
        baseId,
        isDeviceId: item.isDeviceId ?? false,
        textValue: "",
        textValueConvert: "",
        textError: "",
      };
    });

    setParsed(parsedArray);
    return parsedArray;
  };

  const buildRules = (parseTypeId: number, convertTypeId: number): ParseAlgorithmDto[] =>
    parsed.map((item, idx) => ({
      id: item.id,
      startIndex: item.start,
      endIndex: item.end,
      dataTypeId: item.typeId,
      toDataTypeId: item.toTypeId,
      parameterType: item.parameterType!,
      description: item.description ?? "",
      parseDataTypeId: parseTypeId,
      convertDataTypeId: convertTypeId,
      parentItemId: item.baseId,
      index: idx,
      isDeviceId: item.isDeviceId ?? false,
    }));

  const fetchData = async (
    itemId: string,
    typeId: number | null,
    toTypeId: number | null,
    sourceParsed: ParsedRowType[]
  ) => {
    if (typeId == null || toTypeId == null) return;
    const item = sourceParsed.find(p => p.id === itemId);
    const code = item?.textValue;
    if (!code || !item) {
      if (item) item.textValueConvert = "";
      return;
    }
    try {
      const response = await rawDataConvert({ code, type: typeId, toType: toTypeId });
      item.textValueConvert = response.data.success ? (response.data.data ?? "") : "";
    } catch {
      item.textValueConvert = "";
    }
  };

  const fetchByItemId = async (itemId: string, sourceParsed: ParsedRowType[]) => {
    const row = sourceParsed.find(p => p.id === itemId);
    if (!row?.typeId || !row?.toTypeId) return;
    await fetchData(itemId, row.typeId, row.toTypeId, sourceParsed);
  };

  const propagateDependents = async (updated: ParsedRowType[], itemId: string) => {
    const dependentIds = getDependentIds(updated, itemId);
    for (const depId of dependentIds) {
      recomputeTextValue(updated, depId);
      await fetchByItemId(depId, updated);
    }
  };

  const handleAddRow = () => setParsed(prev => [...prev, createEmptyRow()]);

  const handleAddChildRow = (_value: string | undefined, parentId: string) => {
    setParsed(prev => {
      const insertIndex = findInsertIndex(prev, parentId);
      const newRow = createEmptyRow(parentId);
      const updated = [...prev];
      updated.splice(insertIndex, 0, newRow);
      return updated;
    });
  };

  const handleStartChange = async (value: string, itemId: string) => {
    const updated = [...parsed];
    const index = updated.findIndex(p => p.id === itemId);
    if (index === -1) return;

    const start = value === "" ? undefined : Number(value);
    updated[index].start = start;
    updated[index].textValue = computeTextValue(start, updated[index].end, updated[index].baseId, updated, rawData);

    await fetchByItemId(itemId, updated);
    await propagateDependents(updated, itemId);
    setParsed(updated);
  };

  const handleEndChange = async (value: string, itemId: string) => {
    const updated = [...parsed];
    const index = updated.findIndex(p => p.id === itemId);
    if (index === -1) return;

    const end = value === "" ? undefined : Number(value);
    updated[index].end = end;
    updated[index].textValue = computeTextValue(updated[index].start, end, updated[index].baseId, updated, rawData);

    await fetchByItemId(itemId, updated);
    await propagateDependents(updated, itemId);
    setParsed(updated);
  };

  const handleTypeChange = async (typeId: number, toTypeId: number | null, itemId: string) => {
    const updated = parsed.map(item =>
      item.id === itemId ? { ...item, typeId: typeId, toTypeId: toTypeId ?? undefined } : { ...item }
    );
    await fetchByItemId(itemId, updated);
    await propagateDependents(updated, itemId);
    setParsed(updated);
  };

  const handleToTypeChange = async (toTypeId: number, typeId: number | null, itemId: string) => {
    const updated = parsed.map(item =>
      item.id === itemId ? { ...item, typeId: typeId ?? undefined, toTypeId: toTypeId } : { ...item }
    );
    await fetchByItemId(itemId, updated);
    await propagateDependents(updated, itemId);
    setParsed(updated);
  };

  const handleParameterTypeChange = (value: string, itemId: string) => {
    setParsed(prev => prev.map(item => item.id === itemId ? { ...item, parameterType: value } : item));
  };

  const handleDescriptionChange = (value: string, itemId: string) => {
    setParsed(prev => prev.map(item => item.id === itemId ? { ...item, description: value } : item));
  };

  const handleIsDeviceIdChange = (value: boolean, itemId: string) => {
    setParsed(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, isDeviceId: value } : { ...item, isDeviceId: false }
      )
    );
  };

  const handleDeleteParsedRow = (itemId: string) => {
    setParsed(prev => {
      const toDelete = new Set<string>([itemId]);
      let changed = true;
      while (changed) {
        changed = false;
        prev.forEach(item => {
          if (!toDelete.has(item.id) && item.baseId && toDelete.has(item.baseId)) {
            toDelete.add(item.id);
            changed = true;
          }
        });
      }
      return prev.filter(item => !toDelete.has(item.id));
    });
  };

  const fetchConvertedValues = async (sourceParsed: ParsedRowType[]) => {
    const localParsed = sourceParsed.map(p => ({ ...p }));

    for (let i = 0; i < localParsed.length; i++) {
      const row = localParsed[i];
      if (!row.typeId) continue;

      const { start, end, baseId } = row;
      let code = "";

      if (baseId) {
        const baseRow = localParsed.find(p => p.id === baseId);
        if (!baseRow?.textValueConvert) {
          row.textValue = "";
          row.textValueConvert = "";
          continue;
        }
        if (typeof start === "number" && typeof end === "number" && start > 0 && end >= start && end <= baseRow.textValueConvert.length) {
          code = baseRow.textValueConvert.substring(start - 1, end);
        } else {
          row.textValue = "";
          row.textValueConvert = "";
          continue;
        }
      } else {
        if (typeof start === "number" && typeof end === "number" && start > 0 && end >= start && end <= rawData.length) {
          code = rawData.substring(start - 1, end);
        } else {
          row.textValue = "";
          row.textValueConvert = "";
          continue;
        }
      }

      row.textValue = code;
      try {
        const response = await rawDataConvert({ code, type: row.typeId, toType: row.toTypeId });
        row.textValueConvert = response.data?.data ?? "";
      } catch {
        row.textValueConvert = "";
      }
    }

    setParsed(localParsed);
  };

  const resetState = () => {
    setRawData("");
    setBaseRawData("");
    setParsed([]);
    hasFetchedConverted.current = false;
  };

  useEffect(() => {
    if (!open) {
      resetState();
    } else {
      setRawData("");
      setBaseRawData(hexData);
    }
  }, [open, hexData]);

  useEffect(() => {
    if (!parsed.length || !rawData) return;
    if (!hasFetchedConverted.current) {
      fetchConvertedValues(parsed);
      hasFetchedConverted.current = true;
    }
  }, [parsed, rawData]);

  useEffect(() => {
    if (!rawData) return;
    fetchConvertedValues(parsed);
  }, [rawData]);

  return {
    rawData,
    rawBaseData,
    parsed,
    setParsed,
    setRawData,
    handleAddRow,
    handleAddChildRow,
    handleStartChange,
    handleEndChange,
    handleTypeChange,
    handleToTypeChange,
    handleParameterTypeChange,
    handleDescriptionChange,
    handleDeleteParsedRow,
    handleIsDeviceIdChange,
    loadParseAlgorithm,
    buildRules,
  };
};
